import { getColors } from 'theme-colors'
import { apcaContrast, colorToGray, hexToRgb, reversePalette, rgbToHex } from '@bpinternal/shared'
import { LatestWebchatTheme } from '@bpinternal/shared'
import { generateCssString } from './generateCSS'

export function generateThemeStylesheet(theme: LatestWebchatTheme): string {
  const { fontFamily, ...rest } = theme
  const themeOverride = generateThemeOverride(rest)
  return generateCssString('.bpReset', { ...themeOverride, 'font-family': `'${fontFamily}', sans-serif` })
}

export function generateThemeOverride(theme: LatestWebchatTheme): Record<string, string> {
  const { radius, themeMode, color, variant, headerVariant } = theme

  let themeOverride: Record<string, string> = {}
  let primaryPalette: Record<string, string> = {}

  if (color) {
    const primaryColors = getColors(color)
    const colors = themeMode === 'dark' ? reversePalette(primaryColors) : primaryColors

    const grayColors = getColors(rgbToHex(colorToGray(hexToRgb(color))))
    const grays = themeMode === 'dark' ? reversePalette(grayColors) : grayColors

    const backgroundColor = themeMode === 'dark' ? '#101010' : '#fdfdfd'

    primaryPalette = Object.entries({ ...colors, '1': backgroundColor }).reduce(
      (acc, [key, value]) => {
        return { ...acc, [`--bpPrimary-${key}`]: value }
      },
      {} as Record<string, string>
    )
    const grayPalette = Object.entries({ ...grays, '1': backgroundColor }).reduce(
      (acc, [key, value]) => {
        return { ...acc, [`--bpGray-${key}`]: value }
      },
      {} as Record<string, string>
    )

    themeOverride = {
      ...primaryPalette,
      ...grayPalette,
    }
  }
  if (radius) {
    themeOverride = {
      ...themeOverride,
      ...{ '--bpRadius-scale': radius.toString() },
    }
  }

  const transparentColors =
    themeMode === 'dark'
      ? { bg: 'var(--transparent-dark)', bgHover: 'var(--transparent-dark-hover)' }
      : { bg: 'var(--transparent-light)', bgHover: 'var(--transparent-light-hover)' }

  const onSolidTextColor = primaryPalette
    ? apcaContrast(primaryPalette['--bpPrimary-600'], primaryPalette['--bpPrimary-50']) > 40
      ? 'var(--bpPrimary-50)'
      : 'var(--bpPrimary-900)'
    : 'var(--bpPrimary-50)'

  const variantVariables: Record<`--${string}`, string> =
    variant === 'solid'
      ? {
          '--message-bg': 'var(--bpPrimary-600)',
          '--message-bg-hover': 'var(--bpPrimary-700)',
          '--message-text': onSolidTextColor,
          '--send-button-bg': 'var(--bpPrimary-600)',
          '--send-button-bg-hover': 'var(--bpPrimary-700)',
          '--send-button-text': 'var(--bpPrimary-50)',
          '--button-bg': 'var(--bpPrimary-200)',
          '--button-bg-hover': 'var(--bpPrimary-300)',
          '--button-text': 'var(--bpPrimary-800)',
          '--fab-bg': 'var(--bpPrimary-600)',
          '--fab-bg-hover': 'var(--bpPrimary-700)',
          '--fab-icon': onSolidTextColor,
          '--modal-submit-bg': 'var(--bpPrimary-600)',
          '--modal-submit-bg-hover': 'var(--bpPrimary-700)',
        }
      : {
          '--message-bg': 'var(--bpPrimary-100)',
          '--message-bg-hover': 'var(--bpPrimary-200)',
          '--message-text': 'var(--bpPrimary-900)',
          '--send-button-bg': 'var(--bpPrimary-600)',
          '--send-button-bg-hover': 'var(--bpPrimary-700)',
          '--send-button-text': 'var(--bpPrimary-50)',
          '--button-bg': 'var(--bpPrimary-500)',
          '--button-bg-hover': 'var(--bpPrimary-600)',
          '--button-text': primaryPalette
            ? apcaContrast(primaryPalette['--bpPrimary-500'], primaryPalette['--bpPrimary-50']) > 40
              ? 'var(--bpPrimary-50)'
              : 'var(--bpPrimary-900)'
            : 'var(--bpPrimary-50)',
          '--fab-bg': 'var(--bpPrimary-100)',
          '--fab-bg-hover': 'var(--bpPrimary-200)',
          '--fab-icon': 'var(--bpPrimary-700)',
          '--scrollbar': 'var(--bpGray-500)',
          '--scrollbar-hover': 'var(--bpGray-400)',
          '--modal-submit-bg': 'var(--bpPrimary-200)',
          '--modal-submit-bg-hover': 'var(--bpPrimary-300)',
        }

  const headerVariantVariables: Record<`--${string}`, string> =
    headerVariant === 'solid'
      ? {
          '--header-bg': 'var(--bpPrimary-600)',
          '--header-bg-hover': 'var(--bpPrimary-700)',
          '--header-hover-dark': 'var(--bpPrimary-800)',
          '--header-title': onSolidTextColor,
          '--header-description': onSolidTextColor,
          '--header-description-icon': onSolidTextColor,
          '--header-description-link': onSolidTextColor,
          '--header-avatar-bg': 'var(--bpPrimary-300)',
          '--header-avatar-text': 'var(--bpPrimary-700)',
        }
      : {
          '--header-bg': transparentColors.bg,
          '--header-bg-hover': transparentColors.bgHover,
          '--header-hover-dark': 'var(--bpGray-200)',
          '--header-title': 'var(--bpGray-900)',
          '--header-description': 'var(--bpGray-600)',
          '--header-description-icon': 'var(--bpPrimary-700)',
          '--header-description-link': 'var(--bpPrimary-800)',
          '--header-avatar-bg': 'var(--bpPrimary-600)',
          '--header-avatar-text': 'var(--bpPrimary-50)',
        }

  return {
    ...themeOverride,
    ...variantVariables,
    ...headerVariantVariables,
  }
}
